const NON_COMPETITIVE_LEADERBOARD_ROLES = new Set(['admin', 'display', 'host']);
const RESERVED_USERNAMES = Object.freeze(['admin', 'display', 'host']);
const NON_COMPETITIVE_LEADERBOARD_NAMES = new Set(RESERVED_USERNAMES);
const RESERVED_NAME_LENGTHS = new Set(RESERVED_USERNAMES.map(name => name.length));

const isWhitespaceCode = (code) => {
  if (code > 32 && code < 127) return false;
  return (
    code <= 32 ||
    code === 160 ||
    code === 5760 ||
    (code >= 8192 && code <= 8202) ||
    code === 8232 ||
    code === 8233 ||
    code === 8239 ||
    code === 8287 ||
    code === 12288 ||
    code === 65279
  );
};

const hasNonCompetitiveRole = (entry) => {
  if (!entry) return false;
  if (entry.isHost === true || entry.isPrivileged === true) return true;
  const role = entry.role;
  if (typeof role !== 'string' || role.length < 4) return false;
  // Fast path for standard competitive player role
  if (role === 'player') return false;
  // Fast path for exact lowercase/trimmed non-competitive roles
  if (NON_COMPETITIVE_LEADERBOARD_ROLES.has(role)) return true;
  return NON_COMPETITIVE_LEADERBOARD_ROLES.has(role.trim().toLowerCase());
};

const isReservedName = (val) => {
  if (typeof val !== 'string') return false;
  const len = val.length;
  if (len < 4) return false;
  // Fast path for exact lowercase/trimmed non-competitive names
  if (NON_COMPETITIVE_LEADERBOARD_NAMES.has(val)) return true;

  // Optimization: Dynamically check lengths matching RESERVED_USERNAMES.
  // For already-trimmed strings (including unicode whitespace checks), short-circuit
  // if length doesn't match any reserved username length to bypass .toLowerCase() allocations.
  if (!isWhitespaceCode(val.charCodeAt(0)) && !isWhitespaceCode(val.charCodeAt(len - 1))) {
    if (!RESERVED_NAME_LENGTHS.has(len)) return false;
    return NON_COMPETITIVE_LEADERBOARD_NAMES.has(val.toLowerCase());
  }

  const trimmed = val.trim();
  if (!RESERVED_NAME_LENGTHS.has(trimmed.length)) return false;
  return NON_COMPETITIVE_LEADERBOARD_NAMES.has(trimmed.toLowerCase());
};

const hasReservedNonCompetitiveName = (entry) => {
  if (!entry) return false;
  const name = entry.name;
  if (typeof name === 'string' && name.length >= 4 && isReservedName(name)) return true;
  const username = entry.username;
  if (typeof username === 'string' && username.length >= 4 && isReservedName(username)) return true;
  const displayName = entry.displayName;
  if (typeof displayName === 'string' && displayName.length >= 4 && isReservedName(displayName)) return true;
  return false;
};

const isCompetitiveLeaderboardEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return false;

  // If pre-calculated status is explicitly false, it is never competitive.
  if (entry.isCompetitive === false) return false;

  // Optimized path: If isCompetitive is explicitly true, trust it.
  // We ensure the reserved name check is performed during the pre-calculation phase
  // (applyPlayerRoleState) so that the hot path only needs to check this single boolean.
  if (entry.isCompetitive === true) {
    return true;
  }

  return !hasNonCompetitiveRole(entry) && !hasReservedNonCompetitiveName(entry);
};

/**
 * Helper to sanitize entries in a single pass.
 * Returns { list, invalidCount }.
 * Fast-path: If all entries are valid, returns a shallow copy of the array directly
 * without filter/array push overhead.
 */
const sanitizeEntriesFast = (entries) => {
  if (!Array.isArray(entries) || entries.length === 0) {
    return { list: [], invalidCount: 0 };
  }

  let invalidCount = 0;
  let list = null;

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    if (!isCompetitiveLeaderboardEntry(entry)) {
      if (list === null) {
        list = entries.slice(0, i);
      }
      invalidCount++;
    } else if (list !== null) {
      list.push(entry);
    }
  }

  if (list === null) {
    return { list: entries.slice(), invalidCount: 0 };
  }

  return { list, invalidCount };
};

const sanitizeLeaderboardEntries = (entries) =>
  sanitizeEntriesFast(entries).list;

const sanitizeLeaderboardsPayload = (leaderboards) => {
  const parlorResult = sanitizeEntriesFast(leaderboards?.parlor);
  const globalResult = sanitizeEntriesFast(leaderboards?.global);

  return {
    parlor: parlorResult.list,
    global: globalResult.list,
    invalidCount: parlorResult.invalidCount + globalResult.invalidCount,
  };
};

export {
  RESERVED_USERNAMES,
  isCompetitiveLeaderboardEntry,
  sanitizeLeaderboardEntries,
  sanitizeLeaderboardsPayload,
  hasReservedNonCompetitiveName,
};
